import get from 'lodash/get';
import isArray from 'lodash/isArray';
import upperFirst from 'lodash/upperFirst';
import toLower from 'lodash/toLower';
import { AutomationActionType } from '../../types/enums/AutomationActionType';
import {
  AutomationDataActionType,
  AutomationPermissionMode,
  AutomationTypes,
  ScheduledParametersOffsetType,
} from '../../types/enums/AutomationTypes';
import { NestedSelectorPath } from '../common/NestedSelector/NestedSelector';
import { Box, Divider, styled } from '@mui/material';
import { colors, Typography } from '@sweep-io/sweep-design';
import { ReactNode } from 'react';
import { AutomationFormVariant } from '../../types/enums/AutomationFormVariant';
import { AutomationType } from '../../types/enums/AutomationType';
import { SweepSortOption } from '../common/SweepSort';
import { AddDedupMatchingButton } from './AddDedupMatchingButton';
import { AddAutomationButton, AddAutomationButtonProps } from './AddAutomationButton';
import { DedupMatchingType } from '../../types/enums/DedupMatchingType';
import { uniqueId } from '../../lib/uniqueId';
import { DedupMergeActionType } from '../routing/dedupMatching/helpers';
import { PopoverProps } from '@mui/material/Popover';
import { humanizeDate, humanizeDateVariants } from '../helpers/humanizeDate';
import { DateTime } from 'luxon';
import { SweepFieldOperator } from '../SweepFieldsOperatorSelector/sweepFieldsTypesAndOperators';
import { ACTIONS_EVENTS } from '../../services/events';
import flatten from 'lodash/flatten';
import { DeployStatusForTable } from '../../types/enums/DeployStatusForTable';
import { SlackConnectionStatus } from '../../reducers/integrationsReducer';
import {
  validateSlackChannelRef,
  validateSlackActionParams,
  validateTeamsChannelRef,
} from './slack/validateSlack';
import { SFDCObjectType } from '../../types/enums/SFDCObjectType';
import { Commands } from '../../types/enums/Common';
import {
  Automations,
  Copy,
  DelayedRefresh,
  Delete,
  Edit,
  Integrations,
  LightningSlash,
  MicrosoftTeams,
  SlackLogo,
  SweepSolid,
  Wrench,
} from '@sweep-io/sweep-design/dist/icons';
import { OrgDot } from '../common/OrgDot';
import { SortOrder } from '../common/types';
import pluralize from 'pluralize';
import { AutomationDeployAs } from '../../types/enums/AutomationDeployAs';
import { CrmOrg } from '../../types/CrmOrgs';

// TODO remove after testing
export const AUTOMATIONS_LAYOUT_MAX_WIDTH = undefined;
export const SEARCH_BOX_WIDTH = '300px';

enum Constant {
  exact = 'exact',
}

export const validateEmails = (emailsArr: string[]) => {
  const regexExp = /\S+@\S+\.\S+/;
  const isValid = emailsArr.every((x) => regexExp.test(x));
  return isValid;
};

export const getNestedPath = (
  _fieldIds: string | string[] | undefined,
  _fieldLabels: string[] | undefined,
): NestedSelectorPath => {
  if (!_fieldIds || _fieldIds.length === 0 || !_fieldLabels || _fieldLabels.length === 0) {
    return [];
  }
  if (!isArray(_fieldIds)) {
    _fieldIds = [_fieldIds];
  }
  return _fieldIds.map((fieldId, idx) => ({
    label: _fieldLabels[idx + 1] || '',
    value: fieldId,
  }));
};

export const parseTime = (time: string) => {
  const timeInt = parseInt(time);
  const minutes = time.substring(3, 5);
  if (time > '12:00') {
    return `${timeInt === 12 ? timeInt : timeInt - 12}:${minutes} pm`;
  } else {
    return `${timeInt}:${minutes} am`;
  }
};

export const getDisplayLineTextForActionList = (
  actions: AutomationAction[],
  asStringOnly?: boolean,
) => {
  return actions.map((action) => {
    switch (action.actionType) {
      case AutomationActionType.SendEmail:
        return 'Send an email notification';
      case AutomationActionType.CreateRecord:
        return `Create a new ${action?.actionParams?.objectTypeName?.label}`;
      case AutomationActionType.UpdateRecords:
        return 'Update record';
      case AutomationActionType.ConvertLead:
        return 'Convert Lead';
      case AutomationActionType.Slack:
        return 'Post to Slack';
      case AutomationActionType.Assignment:
        return 'Assign record';
      case AutomationActionType.AssignmentRules:
        if (asStringOnly) {
          return [
            'Run assignment rules:',
            action.actionParams.assignmentRules.map((rule, i) => `${i + 1}. ${rule.name}`).flat(),
          ];
        }
        return (
          <Box display="inline-flex" flexDirection="column">
            <Typography variant="body">Run assignment rules:</Typography>
            {action.actionParams.assignmentRules.map((rule, i) => (
              <Typography variant="body" key={rule.name + i}>
                {i + 1}. {rule.name}
              </Typography>
            ))}
          </Box>
        );
      case AutomationActionType.SlackDealRoom:
        return 'Create Slack deal room';
      case AutomationActionType.TeamsDealRoom:
        return 'Create Microsoft Teams deal room';
      case AutomationActionType.SlackAddMember:
      case AutomationActionType.TeamsAddMember:
        return 'Add members to deal room';
      case AutomationActionType.SendReportToSlack:
        if (asStringOnly) {
          return ['Send a Salesforce report:', action.actionParams.reportName];
        }
        return (
          <Box display="inline-flex" flexDirection="column">
            <Typography variant="body">Send a Salesforce report:</Typography>
            <Typography variant="body">{action.actionParams.reportName}</Typography>
          </Box>
        );
      case AutomationActionType.VerifyURL:
        return 'Verify URL';
      case AutomationActionType.SlackRenameDealroom:
      case AutomationActionType.TeamsRenameDealroom:
        return 'Rename deal room';
      case AutomationActionType.SlackArchiveDealroom:
      case AutomationActionType.TeamsArchiveDealroom:
        return 'Archive deal room';
      case AutomationActionType.PrioritizedActions:
        if (asStringOnly) {
          return [
            'Run branch actions:',
            action.actionParams.prioritizedActions
              ?.map((rule, i) => `${i + 1}. ${rule._name}`)
              .flat(),
          ];
        }
        return (
          <Box display="inline-flex" flexDirection="column">
            <Typography variant="body">Run branch actions:</Typography>
            {action.actionParams.prioritizedActions?.map((rule, i) => (
              <Typography variant="body" key={rule._name + i}>
                {i + 1}. {rule._name}
              </Typography>
            ))}
          </Box>
        );
      case AutomationActionType.Webhook:
        return 'Trigger integration';
      case AutomationActionType.RunApex:
        if (asStringOnly) {
          return [
            'Invoke custom Apex:',
            `${action.actionParams.className}, ${action.actionParams.methodName}`,
          ];
        }
        return (
          <Box display="inline-flex" flexDirection="column">
            <Typography variant="body">Invoke custom Apex </Typography>
            <Typography variant="body">{`${action.actionParams.className}, ${action.actionParams.methodName}`}</Typography>
          </Box>
        );
      case AutomationActionType.SendTeamsMessage:
        return 'Post to Microsoft Teams';
      default:
        return '';
    }
  });
};

export const getDisplayLineTexts = (automation: AutomationStructureNew, asStringOnly?: boolean) => {
  let triggerText = '',
    actionText: ReactNode[] = [];
  const { stageEnter, stageExit, fieldUpdate, dateArrive, timeInStep, schedule } =
    automation.automationDetails.automationParams || {};
  switch (automation.automationDetails?.triggerType) {
    case AutomationTypes.StageEnter:
      triggerText = 'When entering ' + stageEnter?.toStage?.stageName;
      break;
    case AutomationTypes.StageExit:
      triggerText = `When exiting ${stageExit?.fromStage?.stageName} and entering ${stageExit?.toStage?.stageName}`;
      break;
    case AutomationTypes.FieldUpdate:
      triggerText =
        'When ' + automation.objectName + '.' + fieldUpdate?._leadingFieldLabels[1] + ' is updated';
      break;
    case AutomationTypes.DateArrive:
      const { offsetValue, offsetType, relativeTime, sourceField } = dateArrive || {};

      if (relativeTime === Constant.exact) {
        triggerText = `When ${automation.objectName}.${sourceField?._leadingFieldLabels[1]} arrives`;
      } else {
        triggerText = `${offsetValue} ${offsetType} ${relativeTime} ${sourceField?._leadingFieldLabels[1]} arrives`;
      }
      break;
    case AutomationTypes.TimeInStep:
      const { offsetValue: _offsetValue, offsetType: _offsetType, stage } = timeInStep || {};
      triggerText = `When ${stage?.stageName} is in the same step for ${_offsetValue} ${_offsetType}`;
      break;
    case AutomationTypes.OnCreate:
      triggerText = 'When a record is created';
      break;
    case AutomationTypes.OnUpdate:
      triggerText = 'When a record is updated';
      break;
    case AutomationTypes.OnUpsert:
      triggerText = 'When a record is created or updated';
      break;
  }
  if (automation.type === AutomationType.Dedupe || automation.type === AutomationType.Matching) {
    triggerText = '';
  }
  if (
    automation.type === AutomationType.ScheduledAssignment ||
    automation.type === AutomationType.ScheduledReport ||
    automation.type === AutomationType.ScheduledJob ||
    automation.automationDetails?.triggerType === AutomationTypes.Scheduled
  ) {
    const offsetRecurring = automation.automationDetails.automationParams.schedule?.offset;
    const startDate = humanizeDate({
      dateOrTimestamp: schedule?.startDate ?? '',
      variant: humanizeDateVariants.SHORT_WITHOUT_TIME,
    });
    const startOn = 'starting on ' + startDate;
    const startTime = parseTime(schedule?.executeTime ?? '');
    if (offsetRecurring) {
      const frequent = offsetRecurring?.offsetValue;
      const isOne = frequent === 1;
      const dateObject = new Date(schedule?.startDate ?? '');
      switch (offsetRecurring?.offsetType) {
        case ScheduledParametersOffsetType.MINUTE:
          triggerText = `Every ${frequent} ${pluralize('minute', frequent)} at ${startTime}, ${startOn}`;
          break;
        case ScheduledParametersOffsetType.HOUR:
          triggerText = `Every ${frequent} ${pluralize('hour', frequent)} at ${startTime}, ${startOn}`;
          break;
        case ScheduledParametersOffsetType.DAY:
          triggerText = isOne
            ? `Daily at ${startTime}, ${startOn}`
            : `Every ${frequent} days at ${startTime}, ${startOn}`;
          break;
        case ScheduledParametersOffsetType.WEEK:
          const weekDays = (offsetRecurring as ScheduleWeekOffset)?.weekDays.map(
            (el) => upperFirst(toLower(el)) + ' ',
          );
          triggerText = isOne
            ? `Weekly on ${weekDays} at ${startTime}, ${startOn}`
            : `Every ${frequent} weeks on ${weekDays} at ${startTime}, ${startOn}`;
          break;
        case ScheduledParametersOffsetType.MONTH:
          triggerText = isOne
            ? `Monthly on day ${dateObject.getDate()} at ${startTime}, ${startOn}`
            : `Every ${frequent} months on day ${dateObject.getDate()} at ${startTime}, ${startOn}`;
          break;
        case ScheduledParametersOffsetType.YEAR:
          triggerText = isOne
            ? `Yearly on ${dateObject.toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
              })} at ${startTime}, ${startOn}`
            : `Every ${frequent} years on ${dateObject.toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
              })} at ${startTime}, ${startOn}`;
          break;
      }
    } else {
      triggerText = `On ${startDate} at ${startTime}`;
    }
  }
  actionText = getDisplayLineTextForActionList(automation.automationDetails.actions, asStringOnly);
  if (automation.type === AutomationType.Dedupe) {
    const _objectNameToDisplay = automation?.objectName.toLocaleLowerCase();
    const getText = () => {
      const propToCheck = (
        automation?.automationDetails?.actions?.[0] as DeduplicationAutomationAction
      )?.actionParams?.mergeActionType;
      switch (propToCheck) {
        case DedupMergeActionType.OVERRIDE_NOTHING:
          return [`Match ${pluralize(_objectNameToDisplay, 2)} and merge manually`];
        case DedupMergeActionType.OVERRIDE_NEW_VALUES:
          return [`Enrich existing ${_objectNameToDisplay} and merge`];
        case DedupMergeActionType.OVERRIDE_ALL:
          return [`Override existing ${_objectNameToDisplay} and merge`];
        case DedupMergeActionType.OVERRIDE_BY_CONVERT:
          return [`Convert ${_objectNameToDisplay} into the duplicate contact`];
        default:
          return [`Match ${pluralize(_objectNameToDisplay, 2)}s and merge manually`];
      }
    };
    actionText = getText();
  }
  if (automation.type === AutomationType.Matching) {
    const getAssignAction = () => {
      return (
        automation?.automationDetails?.actions?.[0] as MatchingAutomationAction
      )?.actionParams?.followUpActions?.[0]?.actions?.find(
        (el) => el.actionType === AutomationActionType.Assignment,
      ) as AssignmentAutomationAction;
    };
    const assignAction = getAssignAction();
    if (!assignAction) {
      actionText = ['Only match'];
    } else if (assignAction?.actionParams?.when) {
      actionText = ['Match and assign (if conditions are met)'];
    } else {
      actionText = ['Match and assign'];
    }
  }
  if (automation.type === AutomationType.ScheduledAssignment) {
    if (
      automation?.automationDetails?.getDataAction?.actionType ===
      AutomationDataActionType.QUERY_RESULTS
    ) {
      actionText = ['Assign records that meet a criteria'];
    } else {
      actionText = ['Assign records from Salesforce report'];
    }
  }

  return { triggerText, actionText };
};

export type AutomationSortOption = SweepSortOption & {
  dataAccessor: string;
  getManualValue?: (automation: AutomationStructureNew) => string;
};

export const isAutomationsInStage = (
  automationDetails: AutomationDetailsDtoNew,
  searchValue: string,
  propToUse: '_stageId' | 'stageName',
) => {
  const isNotFieldUpdate = automationDetails.triggerType !== AutomationTypes.FieldUpdate;
  const isToStage =
    automationDetails.automationParams.stageEnter?.toStage?.[propToUse] === searchValue ||
    automationDetails.automationParams.stageExit?.toStage?.[propToUse] === searchValue;

  const isFromStage =
    automationDetails.automationParams.stageExit?.fromStage?.[propToUse] === searchValue;

  const isInStepFor =
    automationDetails.automationParams.timeInStep?.stage?.[propToUse] === searchValue;

  return isNotFieldUpdate && (isToStage || isFromStage || isInStepFor);
};

export const ANY_STEP_NAME = 'Any step';
export const ANY_STEP_ID = 'ANY_STEP';

export const getStepsFromAutomation = (
  automationDetails: AutomationDetailsDtoNew,
): { value: string; label: string }[] => {
  if (automationDetails.triggerType === AutomationTypes.FieldUpdate) {
    return [];
  }

  const stageEnterValue = automationDetails.automationParams.stageEnter?.toStage?._stageId;
  const stageExitValue = automationDetails.automationParams.stageExit?.toStage?._stageId;
  const stageExitFromValue = automationDetails.automationParams.stageExit?.fromStage?._stageId;
  const timeInStepValue = automationDetails.automationParams.timeInStep?.stage?._stageId;

  const stageEnterLabel = automationDetails.automationParams.stageEnter?.toStage?.stageName;
  const stageExitLabel = automationDetails.automationParams.stageExit?.toStage?.stageName;
  const stageExitFromLabel = automationDetails.automationParams.stageExit?.fromStage?.stageName;
  const timeInStepLabel = automationDetails.automationParams.timeInStep?.stage?.stageName;

  const steps = [
    stageEnterValue ? { value: stageEnterValue, label: stageEnterLabel ?? stageEnterValue } : null,
    stageExitValue ? { value: stageExitValue, label: stageExitLabel ?? stageExitValue } : null,
    stageExitFromValue
      ? { value: stageExitFromValue, label: stageExitFromLabel ?? stageExitFromValue }
      : null,
    timeInStepValue ? { value: timeInStepValue, label: timeInStepLabel ?? timeInStepValue } : null,
  ]
    .filter((item) => item !== null)
    .filter((item) => item.value !== ANY_STEP_NAME && item.value !== ANY_STEP_ID);

  return steps;
};

export const getRecordTypesFromAutomation = (
  automation?: AutomationStructureNew,
): { label: string; value: string; objectName?: string }[] => {
  if (!automation) {
    return [];
  }

  const funnelRecordType = {
    label: automation.funnelRecordType?.label ?? '',
    value: automation.funnelRecordType?.name
      ? getRecordTypeIdentifier(automation.funnelRecordType.name, automation.objectName)
      : '',
    objectName: automation.objectName,
  };

  const recordTypesInDetails =
    automation.automationDetails?.recordTypes?.map((rt) => ({
      label: rt.label ?? '',
      value: rt.name ? getRecordTypeIdentifier(rt.name, automation.objectName) : '',
      objectName: automation.objectName,
    })) ?? [];

  return [funnelRecordType, ...recordTypesInDetails].filter((rt) =>
    !rt ? false : rt.value !== '',
  );
};

export const getRecordTypeIdentifier = (recordTypeName: string, objectName: string) =>
  `${objectName}.${recordTypeName}`;

export const getObjectNameFromRecordTypeId = (id: string) => id.split('.')[0];
export const getRecordTypeNameFromRecordTypeId = (id: string) => id.split('.')[1];

export const getAutomationsForStageId = (
  stageId: string,
  automations?: AutomationStructureNew[],
) => {
  const byStep = automations?.filter((el) => {
    return isAutomationsInStage(el.automationDetails, stageId, '_stageId');
  });
  return byStep ?? [];
};

//According to product, "FunnelAutomation" is : object-level OR record-type level (with this specific RT) OR step-level (steps in this funnel)
export const getFunnelAutomations = ({
  funnelId,
  objectName,
  recordType,
  automations,
}: {
  funnelId: string;
  objectName: string;
  recordType: FunnelRecordType;
  automations?: AutomationStructureNew[];
}) => {
  return automations?.filter((automation) => {
    const isSameObject = automation.objectName === objectName;
    if (!isSameObject) return false;
    const isObjectLevel = isObjectLevelAutomation(automation);
    const isRecordTypeLevel = isRecordTypeLevelAutomation(automation, recordType.name);
    const isStepLevel = isStepLevelAutomation(automation, funnelId);
    return isObjectLevel || isRecordTypeLevel || isStepLevel;
  });
};

export const isObjectLevelAutomation = (automation: AutomationStructureNew) =>
  !automation.automationDetails.funnelId && !automation.automationDetails.recordTypes;

export const isRecordTypeLevelAutomation = (
  automation: AutomationStructureNew,
  recordTypeName: string,
) =>
  !automation.automationDetails.funnelId &&
  automation.automationDetails.recordTypes &&
  automation.automationDetails.recordTypes.map((rt) => rt.name).includes(recordTypeName);

export const isStepLevelAutomation = (
  automation: AutomationStructureNew,
  funnelId: string,
  stepId?: string, //if empty, return all automations for this funnel
) => {
  const isSameFunnel = automation.automationDetails.funnelId === funnelId;
  if (!isSameFunnel) return false;
  const stepsInAutomation = getStepsFromAutomation(automation.automationDetails);
  return stepId ? stepsInAutomation.some((step) => step.value === stepId) : true;
};

export const getAddAutomationButtonByType = ({
  variant,
  props,
}: {
  variant: any;
  props: Omit<AddAutomationButtonProps, 'popoverProps' | 'startIconName'> & {
    isEmptyState: boolean;
  };
}) => {
  const { isEmptyState } = props;
  const popoverProps: Pick<PopoverProps, 'anchorOrigin' | 'transformOrigin' | 'slotProps'> = {
    anchorOrigin: {
      vertical: isEmptyState ? 'top' : 'bottom',
      horizontal: isEmptyState ? 'right' : 'left',
    },
    transformOrigin: {
      vertical: isEmptyState ? 'top' : -4,
      horizontal: isEmptyState ? -4 : 'left',
    },
    slotProps: {
      paper: {
        style: {
          width: '277px',
        },
        sx: {
          marginTop: 0,
        },
      },
    },
  };
  if (variant === AutomationFormVariant.DEDUP_MATCHING) {
    return <AddDedupMatchingButton {...props} startIconName={isEmptyState ? '' : 'Plus'} />;
  } else
    return (
      <AddAutomationButton
        {...props}
        popoverProps={popoverProps}
        startIconName={isEmptyState ? '' : 'Plus'}
      />
    );
};

export const getDedupMatchingDefaultAutomation = (
  type: DedupMatchingType,
): Partial<AutomationStructureNew> => {
  switch (type) {
    case DedupMatchingType.LEAD_TO_LEAD_DEDUP:
      return {
        name: 'Lead to Lead Deduplication',
        objectName: SFDCObjectType.Lead,
        type: AutomationType.Dedupe,
        isActive: true,
        automationDetails: {
          triggerType: AutomationTypes.OnCreate,
          actions: [
            {
              _id: uniqueId(),
              actionType: 'DEDUP',
              actionParams: {
                mergeActionType: DedupMergeActionType.OVERRIDE_NOTHING,
                dmType: DedupMatchingType.LEAD_TO_LEAD_DEDUP,
              },
            },
          ],
          permissionMode: AutomationPermissionMode.SystemMode,
          automationParams: {},
        },
      };

    case DedupMatchingType.CONTACT_TO_CONTACT_DEDUP:
      return {
        name: 'Contact to Contact Deduplication',
        objectName: SFDCObjectType.Contact,
        type: AutomationType.Dedupe,
        isActive: true,
        automationDetails: {
          triggerType: AutomationTypes.OnCreate,
          actions: [
            {
              _id: uniqueId(),
              actionType: 'DEDUP',
              actionParams: {
                mergeActionType: DedupMergeActionType.OVERRIDE_NOTHING,
                dmType: DedupMatchingType.CONTACT_TO_CONTACT_DEDUP,
              },
            },
          ],
          permissionMode: AutomationPermissionMode.SystemMode,
          automationParams: {},
        },
      };

    case DedupMatchingType.ACCOUNT_TO_ACCOUNT_DEDUP:
      return {
        name: 'Account to Account Deduplication',
        objectName: SFDCObjectType.Account,
        type: AutomationType.Dedupe,
        isActive: true,
        automationDetails: {
          triggerType: AutomationTypes.OnCreate,
          actions: [
            {
              _id: uniqueId(),
              actionType: 'DEDUP',
              actionParams: {
                mergeActionType: DedupMergeActionType.OVERRIDE_NOTHING,
                dmType: DedupMatchingType.ACCOUNT_TO_ACCOUNT_DEDUP,
              },
            },
          ],
          permissionMode: AutomationPermissionMode.SystemMode,
          automationParams: {},
        },
      };

    case DedupMatchingType.LEAD_TO_ACCOUNT_MATCHING:
      return {
        name: 'Lead to Account Matching',
        objectName: SFDCObjectType.Lead,
        type: AutomationType.Matching,
        isActive: true,
        automationDetails: {
          triggerType: AutomationTypes.OnUpsert,
          actions: [
            {
              _id: uniqueId(),
              actionType: 'MATCHING',
              actionParams: {
                useContinuousSync: false,
                fieldMapping: [],
                dmType: DedupMatchingType.LEAD_TO_ACCOUNT_MATCHING,
              },
            },
          ],
          permissionMode: AutomationPermissionMode.SystemMode,
          automationParams: {},
        },
      };
    case DedupMatchingType.LEAD_TO_CONTACT_DEDUP:
      return {
        name: 'Lead to Contact Deduplication',
        objectName: SFDCObjectType.Lead,
        type: AutomationType.Dedupe,
        isActive: true,
        automationDetails: {
          triggerType: AutomationTypes.OnCreate,
          actions: [
            {
              _id: uniqueId(),
              actionType: 'DEDUP',
              actionParams: {
                mergeActionType: DedupMergeActionType.OVERRIDE_NOTHING,
                dmType: DedupMatchingType.LEAD_TO_CONTACT_DEDUP,
              },
            },
          ],
          permissionMode: AutomationPermissionMode.SystemMode,
          automationParams: {},
        },
      };
    case DedupMatchingType.LEAD_TO_LEAD_MATCHING:
      return {
        name: 'Lead to Lead Matching',
        objectName: SFDCObjectType.Lead,
        type: AutomationType.Matching,
        isActive: true,
        automationDetails: {
          triggerType: AutomationTypes.OnUpsert,
          actions: [
            {
              _id: uniqueId(),
              actionType: 'MATCHING',
              actionParams: {
                useContinuousSync: false,
                fieldMapping: [],
                dmType: DedupMatchingType.LEAD_TO_LEAD_MATCHING,
              },
            },
          ],
          permissionMode: AutomationPermissionMode.SystemMode,
          automationParams: {},
        },
      };
    case DedupMatchingType.CASE_TO_CASE_DEDUP:
      return {
        name: 'Case to Case deduplication',
        objectName: SFDCObjectType.Case,
        type: AutomationType.Dedupe,
        isActive: true,
        automationDetails: {
          triggerType: AutomationTypes.OnCreate,
          actions: [
            {
              _id: uniqueId(),
              actionType: 'DEDUP',
              actionParams: {
                mergeActionType: DedupMergeActionType.OVERRIDE_NOTHING,
                dmType: DedupMatchingType.CASE_TO_CASE_DEDUP,
                matchingLogic: {
                  criteriaLogic: '1',
                  criteria: [
                    {
                      criterionId: uniqueId(),
                      fieldType: '',
                      operator: '',
                      value: '',
                      _fieldIds: [],
                      valueType: '',
                    },
                  ],
                },
              },
            },
          ],
          permissionMode: AutomationPermissionMode.SystemMode,
          automationParams: {},
        },
      };
    case DedupMatchingType.OPP_TO_OPP_DEDUP:
      return {
        name: 'Opportunity to Opportunity deduplication',
        objectName: SFDCObjectType.Opportunity,
        type: AutomationType.Dedupe,
        isActive: true,
        automationDetails: {
          triggerType: AutomationTypes.OnCreate,
          actions: [
            {
              _id: uniqueId(),
              actionType: 'DEDUP',
              actionParams: {
                mergeActionType: DedupMergeActionType.OVERRIDE_NOTHING,
                dmType: DedupMatchingType.OPP_TO_OPP_DEDUP,
                matchingLogic: {
                  criteriaLogic: '1',
                  criteria: [
                    {
                      criterionId: uniqueId(),
                      fieldType: '',
                      operator: '',
                      value: '',
                      _fieldIds: [],
                      valueType: '',
                    },
                  ],
                },
              },
            },
          ],
          permissionMode: AutomationPermissionMode.SystemMode,
          automationParams: {},
        },
      };
  }
};

export const getTypeNameFromVariant = (automationVariant: AutomationFormVariant) => {
  switch (automationVariant) {
    case AutomationFormVariant.PLAYBOOK_ALERT:
      return 'alert';
    case AutomationFormVariant.ASSIGNMENT_RULES:
      return 'assignment rule';
    case AutomationFormVariant.DEDUP_MATCHING:
      return 'dedupe matching';
    default:
      return 'automation';
  }
};

export const sortAutomations = ({
  sortBy,
  automations,
  selectedSortOption,
  sortOrder,
}: {
  sortBy?: string;
  automations: AutomationStructureNew[];
  selectedSortOption: any;
  sortOrder?: SortOrder;
}) => {
  return sortBy
    ? automations.toSorted((automationA, automationB) => {
        let aValue, bValue;
        if (selectedSortOption?.getManualValue) {
          aValue = selectedSortOption?.getManualValue(automationA);
          bValue = selectedSortOption?.getManualValue(automationB);
        } else {
          aValue = get(automationA, selectedSortOption?.dataAccessor ?? '');
          bValue = get(automationB, selectedSortOption?.dataAccessor ?? '');
        }
        if (typeof aValue === 'boolean') {
          aValue = aValue ? '1' : '0';
        }
        if (typeof bValue === 'boolean') {
          bValue = bValue ? '1' : '0';
        }
        return (
          (sortOrder === SortOrder.DESC
            ? bValue?.localeCompare(aValue)
            : aValue?.localeCompare(bValue)) ?? 1
        );
      })
    : automations;
};

export const getIsScheduleValid = (schedule?: AutomationSchedule) => {
  const forAll = schedule?.startDate && schedule?.executeTime && schedule?.timezone;
  let isOffset = !!schedule?.offset;
  if (isOffset) {
    isOffset =
      schedule?.offset?.offsetType &&
      !!schedule?.offset?.offsetValue &&
      !!(schedule?.offset?.offsetValue >= 1) &&
      (schedule?.offset?.offsetType === ScheduledParametersOffsetType.WEEK
        ? (schedule?.offset as ScheduleWeekOffset)?.weekDays &&
          (schedule?.offset as ScheduleWeekOffset)?.weekDays?.length > 0
        : true);
    if (schedule?.endDate) {
      isOffset =
        !!schedule?.endDate &&
        DateTime.fromISO(schedule?.startDate) < DateTime.fromISO(schedule?.endDate);
    }
  } else {
    isOffset = true;
  }
  return forAll && isOffset;
};

export const getOperatorsWithoutRecordsComparison = (operatorList: OperatorWithIndex[]) => {
  const listOfOperatorsToRemove = [
    SweepFieldOperator.HAS_DECREASED,
    SweepFieldOperator.HAS_INCREASED,
    SweepFieldOperator.IS_CHANGED_FALSE,
    SweepFieldOperator.IS_CHANGED_TRUE,
  ];
  return operatorList.filter(({ operator }) => {
    return !listOfOperatorsToRemove.includes(operator);
  });
};

export const AutomationDeploySuccessEventMap = {
  [AutomationType.Default]: ACTIONS_EVENTS.automationsDeploySuccess,
  [AutomationType.Alert]: ACTIONS_EVENTS.alertsDeploySuccess,
  [AutomationType.Dedupe]: ACTIONS_EVENTS.matchingDedupeDeploySuccess,
  [AutomationType.Matching]: ACTIONS_EVENTS.matchingDedupeDeploySuccess,
  [AutomationType.Assignment]: ACTIONS_EVENTS.assignmentsDeploySuccess,
  [AutomationType.ScheduledAssignment]: ACTIONS_EVENTS.scheduledAssignmentsDeploySuccess,
  [AutomationType.ScheduledReport]: ACTIONS_EVENTS.scheduledReportsDeploySuccess,
  [AutomationType.ScheduledJob]: ACTIONS_EVENTS.scheduledJobsDeploySuccess,
  [AutomationType.MarketingAttributionModel]: ACTIONS_EVENTS.marketingAttributionModelDeploySuccess,
};

export const AutomationAddEventMap = {
  [AutomationFormVariant.AUTOMATIONS]: ACTIONS_EVENTS.automationsAdd,
  [AutomationFormVariant.PLAYBOOK_ALERT]: ACTIONS_EVENTS.alertsAdd,
  [AutomationFormVariant.DEDUP_MATCHING]: ACTIONS_EVENTS.matchingDedupeAdd,
  [AutomationFormVariant.ASSIGNMENT_RULES]: ACTIONS_EVENTS.assignmentsAdd,
  [AutomationFormVariant.SCHEDULED_ASSIGNMENTS]: ACTIONS_EVENTS.scheduledAssignmentsAdd,
  [AutomationFormVariant.SCHEDULED_REPORTS]: ACTIONS_EVENTS.scheduledReportsAdd,
  [AutomationFormVariant.SCHEDULED_JOBS]: ACTIONS_EVENTS.scheduledJobsAdd,
  [AutomationFormVariant.MARKETING_ATTRIBUTIONS]: ACTIONS_EVENTS.marketingAttributionModelAdd,
};

export const AutomationDeleteEventMap = {
  [AutomationFormVariant.AUTOMATIONS]: ACTIONS_EVENTS.automationsDelete,
  [AutomationFormVariant.PLAYBOOK_ALERT]: ACTIONS_EVENTS.alertsDelete,
  [AutomationFormVariant.DEDUP_MATCHING]: ACTIONS_EVENTS.matchingDedupeDelete,
  [AutomationFormVariant.ASSIGNMENT_RULES]: ACTIONS_EVENTS.assignmentsDelete,
  [AutomationFormVariant.SCHEDULED_ASSIGNMENTS]: ACTIONS_EVENTS.scheduledAssignmentsDelete,
  [AutomationFormVariant.SCHEDULED_REPORTS]: ACTIONS_EVENTS.scheduledReportsDelete,
  [AutomationFormVariant.SCHEDULED_JOBS]: ACTIONS_EVENTS.scheduledJobsDelete,
  [AutomationFormVariant.MARKETING_ATTRIBUTIONS]: ACTIONS_EVENTS.marketingAttributionModelDelete,
};

export const getPropsForBiEvent = (automationId: string, items?: AutomationStructureNew[]) => {
  const item = items?.find((item) => item.automationId === automationId);
  return item
    ? {
        isDeployed: [DeployStatusForTable.Deployed, DeployStatusForTable.Pending].includes(
          item.status,
        ),
        object: item.objectName,
      }
    : undefined;
};

export const isAutomationFieldValid = (
  field: AutomationActionField,
  action?: DeepPartial<AutomationAction>,
) => {
  const allowsEmptyFieldValues =
    action?.actionType === AutomationActionType.UpdateRecords ||
    action?.actionType === AutomationActionType.ConvertLead;
  const hasRequiredFields = field._fieldIds.length && field._id;

  if (allowsEmptyFieldValues) {
    // TODO: The logic should instead be like below once we implement the required logic in the backend
    // return field.properties.required ? hasRequiredFields && field.value : hasRequiredFields;
    return hasRequiredFields;
  }

  return hasRequiredFields && field.value;
};

export const validateAnAction = ({
  action,
  slackStatus,
  objectName,
}: {
  action: DeepPartial<AutomationAction>;
  slackStatus: SlackConnectionStatus;
  objectName?: string;
}) => {
  switch (action.actionType) {
    case AutomationActionType.AssignmentRules:
      if (action.actionParams?.assignmentRules && action.actionParams.assignmentRules.length > 0) {
        return true;
      }
      break;
    case AutomationActionType.SendEmail:
      return (
        action.actionParams?.emailTemplate &&
        action.actionParams?.toEmailRecipients &&
        action.actionParams.toEmailRecipients.length > 0
      );
    case AutomationActionType.Slack:
      return validateSlackActionParams(slackStatus, action.actionParams);
      break;
    case AutomationActionType.SendTeamsMessage:
      return validateSlackActionParams(SlackConnectionStatus.Connected, action.actionParams);
    case AutomationActionType.SlackDealRoom:
      if (
        action.actionParams?.members &&
        action.actionParams?.members.length > 0 &&
        action.actionParams?.channel &&
        action.actionParams?.channel?.name &&
        action.actionParams?.channel?.name?.template
      ) {
        return true;
      }
      break;
    case AutomationActionType.SendReportToSlack:
      if (
        action.actionParams?.recipients &&
        action.actionParams?.recipients.length > 0 &&
        action.actionParams?.reportName
      ) {
        return true;
      }
      break;
    case AutomationActionType.SlackAddMember:
      if (
        action.actionParams?.members &&
        action.actionParams?.members.length > 0 &&
        validateSlackChannelRef(action)
      ) {
        return true;
      }
      break;
    case AutomationActionType.SlackRenameDealroom:
      if (
        action.actionParams?.name &&
        action.actionParams?.name?.template &&
        validateSlackChannelRef(action)
      ) {
        return true;
      }
      break;
    case AutomationActionType.TeamsRenameDealroom:
      if (
        action.actionParams?.name &&
        action.actionParams?.name?.template &&
        validateTeamsChannelRef(action)
      ) {
        return true;
      }
      break;
    case AutomationActionType.SlackArchiveDealroom:
      return validateSlackChannelRef(action);
    case AutomationActionType.TeamsArchiveDealroom:
      return validateTeamsChannelRef(action);
    case AutomationActionType.TeamsAddMember:
      if (
        action.actionParams?.members &&
        action.actionParams?.members.length > 0 &&
        validateTeamsChannelRef(action)
      ) {
        return true;
      }
      break;
    case AutomationActionType.TeamsDealRoom:
      if (
        action.actionParams?.members &&
        action.actionParams?.members.length > 0 &&
        action.actionParams?.channelName &&
        action.actionParams?.channelName?.name &&
        action.actionParams?.channelName?.name?.template &&
        action.actionParams?.teamId
      ) {
        return true;
      }
      break;
    case AutomationActionType.ConvertLead:
      if (action.actionParams?.contact && action.actionParams?.contact.recordType === -1) {
        return false;
      }
      if (action.actionParams?.account && action.actionParams?.account.recordType === -1) {
        return false;
      }
      if (action.actionParams?.opportunity && action.actionParams?.opportunity.recordType === -1) {
        return false;
      }
      if (
        action.actionParams?.account?._fields &&
        action.actionParams?.account?._fields?.length > 0
      ) {
        const isInvalidField = action.actionParams.account._fields.find(
          (field) => !isAutomationFieldValid(field, action),
        );
        if (isInvalidField) return false;
      }
      if (
        action.actionParams?.contact?._fields &&
        action.actionParams?.contact?._fields?.length > 0
      ) {
        const isInvalidField = action.actionParams.contact._fields.find(
          (field) => !isAutomationFieldValid(field, action),
        );
        if (isInvalidField) return false;
      }
      if (
        action.actionParams?.opportunity?._fields &&
        action.actionParams?.opportunity?._fields?.length > 0
      ) {
        const isInvalidField = action.actionParams.opportunity._fields.find(
          (field) => !isAutomationFieldValid(field, action),
        );
        if (isInvalidField) return false;
      }
      if (action.actionParams?.convertedStatus) {
        return true;
      }
      break;
    case AutomationActionType.CreateRecord:
    case AutomationActionType.UpdateRecords:
      if (!action.actionParams?._fields) {
        return false;
      }
      if (action.actionParams._fields.length === 0) {
        return false;
      }
      if (
        action.actionParams.relationsRecords &&
        objectName === 'Contact' &&
        action.actionParams.relationsRecords.find(
          (el) => el.objectType === 'OpportunityContactRole',
        )?._isActive &&
        !action.actionParams.relationsRecords.find(
          (el) => el.objectType === 'OpportunityContactRole',
        )?.fields
      ) {
        return false;
      }
      if (action.actionParams.isCriteria && !action.actionParams.criteria) {
        return false;
      }
      return !action.actionParams._fields.find((field) => !isAutomationFieldValid(field, action));
    case AutomationActionType.CreateRecord:
      if (!action.actionParams?.objectTypeName) {
        return false;
      }
      break;
    case AutomationActionType.VerifyURL:
      if (!action.actionParams?.urlFieldId) {
        return false;
      }

      const allValidateActions = flatten(
        action?.actionParams?.verifyResultFollowUps?.map((el) => {
          return flatten(el?.followUpActions?.map((action) => action?.actions));
        }),
      ).filter((el) => !!el);

      if (allValidateActions?.length === 0) {
        return false;
      }
      const areValidUrlActionsValid = allValidateActions.every((action) =>
        validateAnAction({
          action,
          slackStatus,
          objectName,
        }),
      );
      if (areValidUrlActionsValid) {
        return true;
      }
      break;
    case AutomationActionType.PrioritizedActions:
      if (
        action?.actionParams?.prioritizedActions &&
        action?.actionParams?.prioritizedActions?.length > 0
      ) {
        return true;
      }
      break;
    case AutomationActionType.Webhook:
      if (action?.actionParams?.verb) {
        return true;
      }
      break;
    case AutomationActionType.RunApex:
      if (action?.actionParams?.className && action?.actionParams?.methodName) {
        return true;
      }
      break;
    case AutomationActionType.MarketingAttributionModel:
      const modelType = action?.actionParams?.modelType;
      const areWeightsValid =
        modelType === 'MULTI_TOUCH'
          ? Object.values(action?.actionParams?.weights ?? {}).reduce((a, b) => a + b, 0) === 1
          : true;

      if (modelType && areWeightsValid) {
        return true;
      }
      break;
  }
  return false;
};

// The undefined are for actions that we don't show the labels for now
export const AutomationActionDescriptionByType = {
  [AutomationActionType.CreateRecord]: 'Create record',
  [AutomationActionType.UpdateRecords]: 'Update record',
  [AutomationActionType.SendEmail]: 'Send email',
  [AutomationActionType.ConvertLead]: 'Convert lead',
  [AutomationActionType.Slack]: 'Post to Slack',
  [AutomationActionType.SlackDealRoom]: 'Create deal room',
  [AutomationActionType.SlackAddMember]: 'Add members to deal room',
  [AutomationActionType.VerifyURL]: 'Verify URL',
  [AutomationActionType.SlackRenameDealroom]: 'Rename deal room',
  [AutomationActionType.SlackArchiveDealroom]: 'Archive deal room',
  [AutomationActionType.PrioritizedActions]: 'Branch actions',
  [AutomationActionType.Webhook]: 'Trigger integration',
  [AutomationActionType.RunApex]: 'Invoke custom Apex',
  [AutomationActionType.SendTeamsMessage]: 'Post to Teams',
  [AutomationActionType.TeamsAddMember]: 'Add members deal room',
  [AutomationActionType.TeamsDealRoom]: 'Create deal room',
  [AutomationActionType.TeamsRenameDealroom]: 'Rename deal room',
  [AutomationActionType.TeamsArchiveDealroom]: 'Archive deal room',
  [AutomationActionType.PushNotification]: 'Push Notification', // TODO: REMOVE with ft lgDemo
  [AutomationActionType.CloneRecords]: 'Clone Records', // TODO: REMOVE with ft lgDemo
  [AutomationActionType.Assignment]: undefined,
  [AutomationActionType.AssignmentRules]: undefined,
  [AutomationActionType.SendReportToSlack]: undefined,
  [AutomationActionType.MarketingAttributionModel]: undefined,
};

const getDeployNestedTitle = (automation: AutomationStructureNew) => {
  const isDeployedAsFlow = automation.deployedAs === AutomationDeployAs.FLOW;
  if (isDeployedAsFlow) {
    return 'Deploy as Salesforce Flow';
  }
  return `Deploy ${
    automation.type === AutomationType.Default
      ? 'automation'
      : automation.type === AutomationType.ScheduledAssignment
        ? 'scheduled assignment'
        : automation.type.toLowerCase()
  }:`;
};

const getDeployOptions = ({
  automation,
  defaultCreationEnvId,
  productionEnvId,
}: {
  automation: AutomationStructureNew;
  defaultCreationEnvId?: string;
  productionEnvId?: string;
}) => {
  const defaultIsProd = defaultCreationEnvId === productionEnvId;
  const isDeployedAsFlow = automation.deployedAs === AutomationDeployAs.FLOW;

  const showDeployToCurrentEnvironment = true;
  const showDeployToProductionPlusSandEnvironment = !isDeployedAsFlow && !defaultIsProd;

  const totalActionsCount =
    Number(showDeployToCurrentEnvironment) + Number(showDeployToProductionPlusSandEnvironment);

  return {
    actions: {
      showDeployToCurrentEnvironment,
      showDeployToProductionPlusSandEnvironment,
    },
    defaultIsProd,
    isDeployedAsFlow,
    totalActionsCount,
  };
};

const getDeployMenuItem = ({
  automation,
  defaultCreationEnvironment,
  productionEnvironment,
  isAllowedDeploy,
}: {
  automation: AutomationStructureNew;
  defaultCreationEnvironment?: CrmOrg;
  productionEnvironment?: CrmOrg;
  isAllowedDeploy: boolean;
}) => {
  const {
    isDeployedAsFlow,
    defaultIsProd,
    actions: { showDeployToProductionPlusSandEnvironment },
    totalActionsCount,
  } = getDeployOptions({
    automation,
    defaultCreationEnvId: defaultCreationEnvironment?.id,
    productionEnvId: productionEnvironment?.id,
  });

  const showOnlyDefault = totalActionsCount === 1;

  if (showOnlyDefault) {
    const label = isDeployedAsFlow ? 'Deploy as Salesforce Flow' : 'Deploy';
    return {
      label,
      value: 'onDeployDefault',
      disabled: !isAllowedDeploy,
      actionIcon: <SweepSolid size="medium" />,
      isNested: false,
    };
  }

  const nestedActions = [
    {
      label: (
        <Box display="flex" gap={0.5} alignItems="center">
          <Typography variant="body">Deploy to {defaultCreationEnvironment?.name}</Typography>
          {defaultIsProd && <OrgDot />}
        </Box>
      ),
      value: 'onDeployDefault',
    },
  ];
  if (showDeployToProductionPlusSandEnvironment) {
    nestedActions.push({
      label: (
        <Box display="flex" gap={0.5} alignItems="center">
          <Typography variant="body">
            {defaultCreationEnvironment?.name} + {productionEnvironment?.name}
          </Typography>
          <OrgDot />
        </Box>
      ),
      value: 'onDeployBoth',
    });
  }

  return {
    label: 'Deploy',
    value: 'onDeployDefault',
    disabled: !isAllowedDeploy,
    actionIcon: <SweepSolid size="medium" />,
    isNested: true,
    nestedTitle: getDeployNestedTitle(automation),
    nestedActions,
  };
};

export const buildActionsMenuArray = ({
  automation,
  defaultCreationEnvironment,
  productionEnvironment,
  isAllowedEdit,
  isAllowedCreate,
  isAllowedDeploy,
  isAllowedDelete,
}: {
  automation: AutomationStructureNew;
  defaultCreationEnvironment?: CrmOrg;
  productionEnvironment?: CrmOrg;
  isAllowedEdit: boolean;
  isAllowedCreate: boolean;
  isAllowedDeploy: boolean;
  isAllowedDelete: boolean;
}) => {
  const { isActive, status, type } = automation;
  const defaultIsProd = defaultCreationEnvironment?.id === productionEnvironment?.id;
  const showOnlyDefault = defaultIsProd || !productionEnvironment;

  const editAndDuplicateItems = [
    {
      label: Commands.Edit,
      value: Commands.Edit,
      disabled: !isAllowedEdit,
      actionIcon: <Edit size="medium" />,
    },
    {
      label: Commands.Duplicate,
      value: Commands.Duplicate,
      disabled: !isAllowedCreate,
      actionIcon: <Copy size="medium" />,
    },
  ];
  if (
    automation.automationDetails.actions.some(
      (action) =>
        action.actionType === AutomationActionType.Slack ||
        action.actionType === AutomationActionType.SlackDealRoom ||
        action.actionType === AutomationActionType.SlackAddMember ||
        action.actionType === AutomationActionType.SlackRenameDealroom ||
        action.actionType === AutomationActionType.SlackArchiveDealroom ||
        action.actionType === AutomationActionType.SendReportToSlack,
    )
  ) {
    editAndDuplicateItems.push({
      label: Commands.Slack_Activity,
      value: Commands.Slack_Activity,
      disabled: !isAllowedEdit,
      actionIcon: <SlackLogo size="medium" />,
    });
  }
  if (automation.automationDetails.actions.find((action) => action.actionType === 'WEBHOOK')) {
    editAndDuplicateItems.push({
      label: Commands.Integration_Activity,
      value: Commands.Integration_Activity,
      disabled: !isAllowedEdit,
      actionIcon: <Integrations size="medium" />,
    });
  }
  if (
    automation.automationDetails.actions.some(
      (action) =>
        action.actionType === AutomationActionType.SendTeamsMessage ||
        action.actionType === AutomationActionType.TeamsArchiveDealroom ||
        action.actionType === AutomationActionType.TeamsRenameDealroom ||
        action.actionType === AutomationActionType.TeamsAddMember ||
        action.actionType === AutomationActionType.TeamsDealRoom,
    )
  ) {
    editAndDuplicateItems.push({
      label: Commands.MsTeams_Activity,
      value: Commands.MsTeams_Activity,
      disabled: !isAllowedEdit,
      actionIcon: <MicrosoftTeams size="medium" />,
    });
  }
  const isFlow = automation.deployedAs === AutomationDeployAs.FLOW;

  const _showOnlyDefault = showOnlyDefault || isFlow;

  const activationItem = {
    label: isActive ? 'Deactivate' : 'Activate',
    value: 'onToggleDefault',
    disabled: !isAllowedDeploy,
    actionIcon: isActive ? <LightningSlash size="medium" /> : <Automations size="medium" />,
    isNested: !_showOnlyDefault,
    nestedTitle: !_showOnlyDefault
      ? `Deploy as ${isActive ? 'deactivated' : 'activated'} to:`
      : undefined,
    nestedActions: !_showOnlyDefault
      ? [
          {
            label: defaultCreationEnvironment?.name ?? '',
            value: 'onToggleDefault',
          },
          {
            label: (
              <Box display="flex" gap={0.5} alignItems="center">
                <Typography variant="body">
                  {defaultCreationEnvironment?.name} + {productionEnvironment?.name}
                </Typography>
                <OrgDot />
              </Box>
            ),
            value: 'onToggleBoth',
          },
        ]
      : undefined,
  };

  editAndDuplicateItems.push({
    label: Commands.Version_History,
    value: Commands.Version_History,
    disabled: !isAllowedEdit,
    actionIcon: <DelayedRefresh size="medium" />,
  });

  const deleteItem = {
    addBottomDivider: true,
    label: Commands.Delete,
    value: Commands.Delete,
    disabled: !isAllowedDelete,
    actionIcon: <Delete color={colors.blush[600]} size="medium" />,
    actionSx: { color: colors.blush[600] },
  };

  const deployItem = getDeployMenuItem({
    automation,
    defaultCreationEnvironment,
    productionEnvironment,
    isAllowedDeploy,
  });

  const orderItem = {
    label: Commands.Order,
    value: Commands.Order,
    disabled: !isAllowedEdit,
    actionIcon: <Wrench size="medium" />,
  };

  const noActivation =
    status === DeployStatusForTable.Draft || type === AutomationType.MarketingAttributionModel;

  const _actionList = noActivation
    ? [...editAndDuplicateItems, deleteItem, deployItem]
    : [...editAndDuplicateItems, activationItem, deleteItem, deployItem];

  if (
    type === AutomationType.Default ||
    type === AutomationType.Alert ||
    type === AutomationType.Assignment
  ) {
    _actionList.splice(_actionList.length - 2, 0, orderItem);
  }

  return _actionList;
};

export const DividerLighter = styled(Divider)({
  borderColor: colors.grey[120],
});
